import React, { useState, useMemo, useEffect } from "react";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import "./Dashboard.css";

export default function DataBreachReport() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [data, setData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "report_number",
    direction: "asc",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const rowsPerPage = 8;

  // ===================== FETCH =====================
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!isLoggedIn) {
      navigate("/", { replace: true });
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch("/report/data-breach", {
          method: "GET",
        });

        const records = res?.data || [];

        const formatted = records.map((row) => ({
          report_number: row.task_number,
          report_name: row.task_name,
          particular: row.task_particular,
          title: row.task_title || "",
          description: row.task_description || "",
          startDate: row.task_start_date,
          actionDate: row.task_action_date,
          endDate: row.task_end_date,
          status: row.task_status,
          requestDate: row.task_requested_date,
          responseDate: row.task_response_date,
          userName: row.user_name,
          approversEmail: row.task_approvers_email,
          bu: row.bu_name || "-",
          department: row.dept_name || "-",
        }));

        setData(formatted);
      } catch (err) {
        console.error("Error fetching data breach report:", err);
        setError("Failed to fetch data breach report.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // ===================== LOCAL STORAGE SEARCH =====================
  useEffect(() => {
    const savedSearch = localStorage.getItem("searchitem") || "";
    const complianceId = localStorage.getItem("complianceId") || "";
    const taskName = localStorage.getItem("task_name") || "";

    if (taskName.trim()) {
      setSearchTerm(taskName);
      localStorage.removeItem("task_name");
    } else if (complianceId.trim()) {
      setSearchTerm(complianceId);
      localStorage.removeItem("complianceId");
    } else if (savedSearch.trim()) {
      setSearchTerm(savedSearch);
    }
  }, []);

  // ===================== SEARCH =====================
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    const term = searchTerm.toLowerCase();

    return data.filter(
      (item) =>
        item.report_number?.toString().includes(term) ||
        item.report_name?.toLowerCase().includes(term) ||
        item.particular?.toLowerCase().includes(term) ||
        item.title?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  // ===================== SORT =====================
  const sortedData = useMemo(() => {
    let sortable = [...filteredData];

    sortable.sort((a, b) => {
      const valA = (a[sortConfig.key] || "").toString().toLowerCase();
      const valB = (b[sortConfig.key] || "").toString().toLowerCase();

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return sortable;
  }, [filteredData, sortConfig]);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // ===================== STATUS =====================
  const statusCounts = useMemo(() => {
    const counts = { pending: 0, requested: 0, approved: 0, total: 0 };

    filteredData.forEach((item) => {
      counts.total++;
      const s = item.status?.toLowerCase();
      if (s === "pending") counts.pending++;
      else if (s === "requested") counts.requested++;
      else if (s === "approved") counts.approved++;
    });

    return counts;
  }, [filteredData]);

  // ===================== PAGINATION =====================
  const indexOfLastRow = currentPage * rowsPerPage;
  const currentRows = sortedData.slice(indexOfLastRow - rowsPerPage, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // ===================== EXPORT =====================
  const exportToCSV = () => {
    const headers = [
      "Report Number",
      "Report Name",
      "Particular",
      "Title",
      "Description",
      "Start Date",
      "Action Date",
      "End Date",
      "Status",
      "User",
      "BU",
      "Department",
      "Approver Email",
      "Request Date",
      "Response Date",
    ];

    const rows = data.map((r) => [
      r.report_number,
      r.report_name,
      r.particular,
      r.title,
      r.description,
      r.startDate,
      r.actionDate,
      r.endDate,
      r.status,
      r.userName,
      r.bu,
      r.department,
      r.approversEmail,
      r.requestDate,
      r.responseDate,
    ]);

    const csv = [headers, ...rows]
      .map((e) => e.map((v) => `"${v ?? ""}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "data_breach_report.csv";
    link.click();
  };

  // ===================== UI =====================
  return (
    <div className="general_">
      <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">Data Breach Report</div>
      </div>

      <div className="table-header" style={{ display: "flex", justifyContent: "space-between" }}>
        
        {/* EMPTY DIV (LEFT PUSH) */}
        <div style={{ width: "200px" }}></div>

        <div className="table-actions" style={{ display: "flex", gap: "15px", alignItems: "center" }}>

          {/* STATUS TILES */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ background: "#e2e3e5", color: "#383d41", padding: "6px 12px", borderRadius: "20px", fontWeight: 600, fontSize: "13px" }}>
              Total: {statusCounts.total}
            </div>
            <div style={{ background: "#f8d7da", color: "#721c24", padding: "6px 12px", borderRadius: "20px", fontWeight: 600, fontSize: "13px" }}>
              Pending: {statusCounts.pending}
            </div>
            <div style={{ background: "#fff3cd", color: "#856404", padding: "6px 12px", borderRadius: "20px", fontWeight: 600, fontSize: "13px" }}>
              Requested: {statusCounts.requested}
            </div>
            <div style={{ background: "#d4edda", color: "#155724", padding: "6px 12px", borderRadius: "20px", fontWeight: 600, fontSize: "13px" }}>
              Approved: {statusCounts.approved}
            </div>
          </div>

          {/* SEARCH */}
          <div className="search-box">
            <input
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch className="search-icon" />
          </div>

          {/* EXPORT */}
          <button className="action-btn primary" onClick={exportToCSV}>
            Export
          </button>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("report_number")}>
              Report Number {getSortIcon("report_number")}
            </th>
            <th onClick={() => requestSort("report_name")}>
              Report Name {getSortIcon("report_name")}
            </th>
            <th>Instance</th>
            <th onClick={() => requestSort("startDate")}>Start Date</th>
            <th onClick={() => requestSort("actionDate")}>Action Date</th>
            <th onClick={() => requestSort("endDate")}>End Date</th>
            <th onClick={() => requestSort("status")}>Status</th>
            <th>User</th>
            <th>Approver's Email</th>
            <th onClick={() => requestSort("requestDate")}>Request Date</th>
            <th onClick={() => requestSort("responseDate")}>Response Date</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr><td colSpan="11" style={{ textAlign: "center" }}>Loading...</td></tr>
          ) : error ? (
            <tr><td colSpan="11" style={{ color: "red" }}>{error}</td></tr>
          ) : currentRows.length > 0 ? (
            currentRows.map((row, i) => (
              <tr key={i}>
                <td>{row.report_number}</td>
                <td>{row.report_name}</td>

                {/* INSTANCE */}
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>
                      {row.title
                        ? `${row.particular} - ${row.title}`
                        : row.particular}
                    </span>

                    {row.description && (
                      <IoIosInformationCircle title={row.description} />
                    )}
                  </div>
                </td>

                <td>{row.startDate}</td>
                <td>{row.actionDate}</td>
                <td>{row.endDate}</td>

                <td style={{
                  color:
                    row.status?.toLowerCase() === "approved"
                      ? "green"
                      : row.status?.toLowerCase() === "requested"
                      ? "#f0ad4e"
                      : "red",
                  fontWeight: 600
                }}>
                  {row.status}
                </td>

                {/* USER WITH TOOLTIP */}
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>{row.userName}</span>

                    <IoIosInformationCircle
                      title={`BU: ${row.bu} | Dept: ${row.department}`}
                    />
                  </div>
                </td>

                <td>{row.approversEmail}</td>
                <td>{row.requestDate}</td>
                <td>{row.responseDate}</td>
              </tr>
            ))
          ) : (
            <tr><td colSpan="11">No records found</td></tr>
          )}
        </tbody>
      </table>

      <div className="pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}